import React, { useState, useEffect } from "react";
import { Modal, Form, Alert } from "react-bootstrap";
import * as Fa from "react-icons/fa";
import Axios from "../../api/Axios";
import storage from "../../Services/Storage";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader";
import { format } from "date-fns";
import HeaderFooterEditor, { isHtmlContentEmpty } from "../../Components/HeaderFooterEditor";
import "./InvoiceTemplateManager.scss";

const InvoiceTemplateManager = () => {
    const date = new Date();
    const invoicedate = format(date, "dd/MM/yyyy");
    const auth = storage.getLocal("user");
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [activeTemplateId, setActiveTemplateId] = useState(null);

    // Form state
    const [templateName, setTemplateName] = useState("");
    const [headerImage, setHeaderImage] = useState(null);
    const [footerImage, setFooterImage] = useState(null);
    const [headerPreview, setHeaderPreview] = useState("");
    const [footerPreview, setFooterPreview] = useState("");
    const [validationError, setValidationError] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    // Custom text+logo design mode (alternative to uploading a ready-made image)
    const [headerMode, setHeaderMode] = useState("image"); // "image" | "custom"
    const [footerMode, setFooterMode] = useState("image"); // "image" | "custom"
    const [headerContentHtml, setHeaderContentHtml] = useState("");
    const [footerContentHtml, setFooterContentHtml] = useState("");
    const [headerOverflow, setHeaderOverflow] = useState(false);
    const [footerOverflow, setFooterOverflow] = useState(false);

    // Image dimension constraints (in pixels)
    const HEADER_HEIGHT_PX = 113; // ~3cm at 96 DPI
    const FOOTER_HEIGHT_PX = 246; // ~6.5cm at 96 DPI
    const IMAGE_WIDTH_PX = 794; // A4 width at 96 DPI
    const TOLERANCE = 20; // Allow 20px tolerance

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(`InvoiceTemplate/GetTemplates?CompId=${auth.CompId}`);
            setTemplates(response.data || []);

            // Find active template from API response
            const activeTemplate = response.data?.find(t => t.IsActive === true);
            setActiveTemplateId(activeTemplate?.TemplateId || "none");
        } catch (error) {
            toast.error("Failed to fetch templates", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });
        } finally {
            setLoading(false);
        }
    };

    const validateImageDimensions = (file, type) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const expectedHeight = type === "header" ? HEADER_HEIGHT_PX : FOOTER_HEIGHT_PX;

                if (Math.abs(img.height - expectedHeight) > TOLERANCE) {
                    reject(`${type === "header" ? "Header" : "Footer"} image should be approximately ${expectedHeight}px (3cm for header, 6.5cm for footer) in height. Current: ${img.height}px`);
                } else if (Math.abs(img.width - IMAGE_WIDTH_PX) > TOLERANCE) {
                    reject(`Image width should be approximately ${IMAGE_WIDTH_PX}px (A4 width). Current: ${img.width}px`);
                } else {
                    resolve(true);
                }
            };
            img.onerror = () => reject("Failed to load image");
            img.src = URL.createObjectURL(file);
        });
    };

    const handleImageChange = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setValidationError("Image size should be less than 2MB");
            return;
        }

        // Check file type
        if (!file.type.startsWith("image/")) {
            setValidationError("Please upload a valid image file");
            return;
        }

        try {
            await validateImageDimensions(file, type);

            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === "header") {
                    setHeaderImage(file);
                    setHeaderPreview(reader.result);
                } else {
                    setFooterImage(file);
                    setFooterPreview(reader.result);
                }
                setValidationError("");
            };
            reader.readAsDataURL(file);
        } catch (error) {
            setValidationError(error);
            if (type === "header") {
                setHeaderImage(null);
                setHeaderPreview("");
            } else {
                setFooterImage(null);
                setFooterPreview("");
            }
        }
    };

    const uploadToBlob = async (file, fileName) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileName", fileName);
        formData.append("container", "invoice-templates");

        const response = await Axios.post("Blob/UploadImage", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data.BlobUrl;
    };

    const handleSave = async () => {
        if (!templateName.trim()) {
            setValidationError("Template name is required");
            return;
        }

        // A template has "content" for header/footer either as an uploaded image,
        // or as custom formatted text/logo design - depending on the selected mode.
        const hasHeaderContent = headerMode === "image"
            ? !!(headerImage || currentTemplate?.HeaderImageUrl)
            : !isHtmlContentEmpty(headerContentHtml);
        const hasFooterContent = footerMode === "image"
            ? !!(footerImage || currentTemplate?.FooterImageUrl)
            : !isHtmlContentEmpty(footerContentHtml);

        if (!hasHeaderContent && !hasFooterContent) {
            setValidationError("At least one of header or footer (uploaded image or custom design) is required");
            return;
        }

        if (headerMode === "custom" && headerOverflow) {
            setValidationError("Header content is taller than the allowed height. Please shorten it before saving.");
            return;
        }

        if (footerMode === "custom" && footerOverflow) {
            setValidationError("Footer content is taller than the allowed height. Please shorten it before saving.");
            return;
        }

        if (!isEdit && templates.length >= 3) {
            setValidationError("Maximum 3 templates allowed per company");
            return;
        }

        setLoading(true);
        try {
            let headerUrl = currentTemplate?.HeaderImageUrl || "";
            let footerUrl = currentTemplate?.FooterImageUrl || "";

            // Upload new images if provided (only relevant in "image" mode)
            if (headerMode === "image" && headerImage) {
                const headerFileName = `header_${auth.CompId}_${Date.now()}.${headerImage.name.split(".").pop()}`;
                headerUrl = await uploadToBlob(headerImage, headerFileName);
            }

            if (footerMode === "image" && footerImage) {
                const footerFileName = `footer_${auth.CompId}_${Date.now()}.${footerImage.name.split(".").pop()}`;
                footerUrl = await uploadToBlob(footerImage, footerFileName);
            }

            const templateData = {
                templateId: currentTemplate?.TemplateId || 0,
                compId: auth.CompId,
                templateName: templateName,
                headerType: headerMode, // "image" | "custom"
                headerImageUrl: headerMode === "image" ? headerUrl : "",
                headerContentHtml: headerMode === "custom" ? headerContentHtml : "",
                footerType: footerMode, // "image" | "custom"
                footerImageUrl: footerMode === "image" ? footerUrl : "",
                footerContentHtml: footerMode === "custom" ? footerContentHtml : "",
                isActive: false,
                createdBy: auth.UserId,
            };

            const endpoint = isEdit ? "InvoiceTemplate/UpdateTemplate" : "InvoiceTemplate/CreateTemplate";
            await Axios.post(endpoint, templateData);

            toast.success(`Template ${isEdit ? "updated" : "created"} successfully`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });

            handleCloseModal();
            fetchTemplates();
        } catch (error) {
            toast.error(error.response?.data?.Message || "Failed to save template", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (template) => {
        setCurrentTemplate(template);
        setTemplateName(template.TemplateName);
        setHeaderMode(template.HeaderType === "custom" ? "custom" : "image");
        setFooterMode(template.FooterType === "custom" ? "custom" : "image");
        setHeaderPreview(template.HeaderImageUrl || "");
        setFooterPreview(template.FooterImageUrl || "");
        setHeaderContentHtml(template.HeaderContentHtml || "");
        setFooterContentHtml(template.FooterContentHtml || "");
        setHeaderOverflow(false);
        setFooterOverflow(false);
        setIsEdit(true);
        setShowModal(true);
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;

        setLoading(true);
        try {
            await Axios.delete(`InvoiceTemplate/DeleteTemplate?templateId=${templateId}`);

            // If deleting the active template, reset to "none"
            if (activeTemplateId === templateId) {
                handleSetActiveTemplate("none");
            }

            toast.success("Template deleted successfully", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });
            fetchTemplates();
        } catch (error) {
            toast.error("Failed to delete template", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSetActiveTemplate = async (templateId) => {
        setLoading(true);
        try {
            // Call API to set active template
            await Axios.get(`InvoiceTemplate/SetActiveTemplate?compId=${auth.CompId}&templateId=${templateId}`);

            if (templateId === "none") {
                toast.success("Switched to default print (no template)", {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                });
            } else {
                const template = templates.find(t => t.TemplateId === templateId);
                if (template) {
                    toast.success(`Template "${template.TemplateName}" activated`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                    });
                }
            }

            // Refresh templates to get updated IsActive status
            fetchTemplates();
        } catch (error) {
            toast.error("Failed to set active template", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
            });
        } finally {
            setLoading(false);
        }
    };

    const resetContentDesignState = () => {
        setHeaderMode("image");
        setFooterMode("image");
        setHeaderContentHtml("");
        setFooterContentHtml("");
        setHeaderOverflow(false);
        setFooterOverflow(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setShowPreviewModal(false);
        setIsEdit(false);
        setCurrentTemplate(null);
        setTemplateName("");
        setHeaderImage(null);
        setFooterImage(null);
        setHeaderPreview("");
        setFooterPreview("");
        setValidationError("");
        resetContentDesignState();
    };

    const handleCreateNew = () => {
        setIsEdit(false);
        setCurrentTemplate(null);
        setTemplateName("");
        setHeaderImage(null);
        setFooterImage(null);
        setHeaderPreview("");
        setFooterPreview("");
        setValidationError("");
        resetContentDesignState();
        setShowModal(true);
    };

    const handlePreviewTemplate = (template) => {
        setHeaderMode(template.HeaderType === "custom" ? "custom" : "image");
        setFooterMode(template.FooterType === "custom" ? "custom" : "image");
        setHeaderPreview(template.HeaderImageUrl || "");
        setFooterPreview(template.FooterImageUrl || "");
        setHeaderContentHtml(template.HeaderContentHtml || "");
        setFooterContentHtml(template.FooterContentHtml || "");
        setShowPreviewModal(true);
    };

    const templateHasHeader = (t) =>
        t?.HeaderType === "custom" ? !isHtmlContentEmpty(t?.HeaderContentHtml) : !!t?.HeaderImageUrl;
    const templateHasFooter = (t) =>
        t?.FooterType === "custom" ? !isHtmlContentEmpty(t?.FooterContentHtml) : !!t?.FooterImageUrl;

    // Derived for whichever header/footer is currently loaded into the modal (create/edit/preview)
    const headerHasContent = headerMode === "custom" ? !isHtmlContentEmpty(headerContentHtml) : !!headerPreview;
    const footerHasContent = footerMode === "custom" ? !isHtmlContentEmpty(footerContentHtml) : !!footerPreview;

    if (loading && templates.length === 0) {
        return <Loader />;
    }

    return (
        <div className="col-12 pt-0 pb-4">
            <div className="row">
                <div className="col-lg-6 col-md-6 col-sm-12">
                    <h3 className="py-2">Invoice Template Manager</h3>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12">
                    <div className="float-end mb-2">
                        <button type="submit" className="btn btn-primary" onClick={handleCreateNew}
                            disabled={templates.length >= 3} >
                            Create New Template <Fa.FaPlus className="ms-2" />
                        </button>
                    </div>
                </div>
            </div>

            <section className="sales-box">
                <div className="itm-page">
                    {templates.length >= 3 && (
                        <div className="itm-banner itm-banner--warning">
                            <Fa.FaExclamationTriangle />
                            <span>Maximum template limit reached (3/3). Delete a template to create a new one.</span>
                        </div>
                    )}

                    <div className="itm-banner itm-banner--active">
                        <Fa.FaCheckCircle />
                        <span>
                            <strong>Active template:</strong>{" "}
                            {activeTemplateId === "none" ? "Default Print (No Template)" : templates.find(t => t.TemplateId === activeTemplateId)?.TemplateName || "None selected"}
                        </span>
                    </div>

                    <div className="itm-grid">
                        {/* Default No Template Option */}
                        <div className={`itm-card ${activeTemplateId === "none" ? "itm-card--active" : ""}`}>
                            <div className="itm-card-body">
                                <div className="itm-card-title-row">
                                    <p className="itm-card-title">Default Print (No Template)</p>
                                    {activeTemplateId === "none" && <span className="itm-badge">Active</span>}
                                </div>
                                <p className="itm-card-meta">No header or footer images will be used</p>
                                <div className="itm-plain-invoice">
                                    <Fa.FaFileAlt size={40} />
                                    <p>Plain Invoice</p>
                                </div>
                                <div className="itm-card-actions">
                                    {activeTemplateId !== "none" ? (
                                        <button className="itm-btn itm-btn-use" onClick={() => handleSetActiveTemplate(0)}>
                                            <Fa.FaCheck /> Use This
                                        </button>
                                    ) : (
                                        <button className="itm-btn itm-btn-active" disabled>
                                            <Fa.FaCheckCircle /> Currently Active
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {templates.map((template) => (
                            <div
                                className={`itm-card ${activeTemplateId === template.TemplateId ? "itm-card--active" : ""}`}
                                key={template.TemplateId}
                            >
                                <div className="itm-card-body">
                                    <div className="itm-card-title-row">
                                        <p className="itm-card-title">{template.TemplateName}</p>
                                        {activeTemplateId === template.TemplateId && <span className="itm-badge">Active</span>}
                                    </div>
                                    <div className="itm-card-meta">
                                        <Fa.FaCheckCircle />
                                        {templateHasHeader(template) && templateHasFooter(template)
                                            ? "Header & Footer"
                                            : templateHasHeader(template)
                                                ? "Header Only"
                                                : "Footer Only"}
                                        {(template.HeaderType === "custom" || template.FooterType === "custom") && (
                                            <span className="itm-badge itm-badge--custom ms-2">Custom design</span>
                                        )}
                                    </div>

                                    {templateHasHeader(template) && (
                                        <div className="itm-preview-block">
                                            <p className="itm-preview-label">Header preview</p>
                                            {template.HeaderType === "custom" ? (
                                                <div
                                                    className="itm-preview-img itm-preview-img--header itm-preview-custom"
                                                    dangerouslySetInnerHTML={{ __html: template.HeaderContentHtml }}
                                                />
                                            ) : (
                                                <img
                                                    src={template.HeaderImageUrl}
                                                    alt="Header"
                                                    className="itm-preview-img itm-preview-img--header"
                                                />
                                            )}
                                        </div>
                                    )}
                                    {templateHasFooter(template) && (
                                        <div className="itm-preview-block">
                                            <p className="itm-preview-label">Footer preview</p>
                                            {template.FooterType === "custom" ? (
                                                <div
                                                    className="itm-preview-img itm-preview-img--footer itm-preview-custom"
                                                    dangerouslySetInnerHTML={{ __html: template.FooterContentHtml }}
                                                />
                                            ) : (
                                                <img
                                                    src={template.FooterImageUrl}
                                                    alt="Footer"
                                                    className="itm-preview-img itm-preview-img--footer"
                                                />
                                            )}
                                        </div>
                                    )}

                                    <div className="itm-card-actions">
                                        {activeTemplateId !== template.TemplateId ? (
                                            <button className="itm-btn itm-btn-use" onClick={() => handleSetActiveTemplate(template.TemplateId)}>
                                                <Fa.FaCheck /> Use This
                                            </button>
                                        ) : (
                                            <button className="itm-btn itm-btn-active" disabled>
                                                <Fa.FaCheck /> Use This
                                            </button>
                                        )}
                                        <button className="itm-btn itm-btn-outline" onClick={() => handlePreviewTemplate(template)}>
                                            <Fa.FaEye /> Preview
                                        </button>
                                        <button className="itm-btn itm-btn-outline" onClick={() => handleEdit(template)}>
                                            <Fa.FaEdit /> Edit
                                        </button>
                                        <button className="itm-btn itm-btn-danger-outline" onClick={() => handleDelete(template.TemplateId)}>
                                            <Fa.FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {templates.length === 0 && (
                        <div className="itm-empty">
                            <Fa.FaFileImage size={56} />
                            <h5>No templates found</h5>
                            <p>Create your first invoice template to get started</p>
                        </div>
                    )}

                    {/* Create/Edit Modal */}
                    <Modal className="itm-modal" show={showModal} onHide={handleCloseModal} size="xl" backdrop="static">
                        <Modal.Header closeButton>
                            <Modal.Title>{isEdit ? "Edit" : "Create"} Invoice Template</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {validationError && (
                                <Alert variant="danger" className="p-2" onClose={() => setValidationError("")} dismissible>
                                    {validationError}
                                </Alert>
                            )}

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Template Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        placeholder="Enter template name"
                                    />
                                </Form.Group>

                                <div className="itm-requirements alert alert-secondary p-2 rounded">
                                    <p className="itm-requirements-title mx-2"><Fa.FaInfoCircle className="me-2"/> Header / footer requirements</p>
                                    <ul className="mb-0" style={{ listStyleType: "disc" }}>
                                        <li style={{ listStyleType: "disc" }}>For each of header and footer, either upload a ready-made image, or design one with formatted text and your logo</li>
                                        <li style={{ listStyleType: "disc" }}>Header height: approximately 794 × 113px (3cm) — optional</li>
                                        <li style={{ listStyleType: "disc" }}>Footer height: approximately 794 × 246px (6.5cm) — optional</li>
                                        <li style={{ listStyleType: "disc" }}><strong>At least one of header or footer is required</strong></li>
                                        <li style={{ listStyleType: "disc" }}>Uploaded images: max 2MB, JPG/PNG/GIF. Logos inserted into a custom design: max 1MB</li>
                                        <li style={{ listStyleType: "disc" }}>Custom designs are clipped to the same height as an uploaded image would be — the editor warns you if your content is too tall</li>
                                        <li style={{ listStyleType: "disc" }}><b>Default Text Settings of Invoice :</b> Font Name – Arial | Font Size – 8px | Font Colour – Black.</li>
                                    </ul>
                                </div>

                                <div className="itm-upload-block mb-4">
                                    <div className="itm-mode-toggle mb-2">
                                        <button
                                            type="button"
                                            className={`itm-mode-btn ${headerMode === "image" ? "itm-mode-btn--active" : ""}`}
                                            onClick={() => setHeaderMode("image")}
                                        >
                                            <Fa.FaImage className="me-1" /> Header: Upload Image
                                        </button>
                                        <button
                                            type="button"
                                            className={`itm-mode-btn ${headerMode === "custom" ? "itm-mode-btn--active" : ""}`}
                                            onClick={() => setHeaderMode("custom")}
                                        >
                                            <Fa.FaFont className="me-1" /> Header: Design Text & Logo
                                        </button>
                                    </div>

                                    {headerMode === "image" ? (
                                        <>
                                            <Form.Group>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="file-upload"
                                                    onChange={(e) => handleImageChange(e, "header")}
                                                />
                                            </Form.Group>
                                            {headerPreview && (
                                                <div className="mt-3">
                                                    <p className="itm-preview-label">Header preview</p>
                                                    <img src={headerPreview} alt="Header Preview" className="itm-preview-img itm-preview-img--header" />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <HeaderFooterEditor
                                            label="Header"
                                            value={headerContentHtml}
                                            onChange={setHeaderContentHtml}
                                            heightPx={HEADER_HEIGHT_PX}
                                            onOverflowChange={setHeaderOverflow}
                                            onUploadLogo={(file) =>
                                                uploadToBlob(file, `logo_header_${auth.CompId}_${Date.now()}.${file.name.split(".").pop()}`)
                                            }
                                            placeholder="Type your header text, and use the image icon in the toolbar to insert your logo…"
                                        />
                                    )}
                                </div>

                                <div className="itm-upload-block mb-3">
                                    <div className="itm-mode-toggle mb-2">
                                        <button
                                            type="button"
                                            className={`itm-mode-btn ${footerMode === "image" ? "itm-mode-btn--active" : ""}`}
                                            onClick={() => setFooterMode("image")}
                                        >
                                            <Fa.FaImage className="me-1" /> Footer: Upload Image
                                        </button>
                                        <button
                                            type="button"
                                            className={`itm-mode-btn ${footerMode === "custom" ? "itm-mode-btn--active" : ""}`}
                                            onClick={() => setFooterMode("custom")}
                                        >
                                            <Fa.FaFont className="me-1" /> Footer: Design Text & Logo
                                        </button>
                                    </div>

                                    {footerMode === "image" ? (
                                        <>
                                            <Form.Group>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="file-upload"
                                                    onChange={(e) => handleImageChange(e, "footer")}
                                                />
                                            </Form.Group>
                                            {footerPreview && (
                                                <div className="mt-3">
                                                    <p className="itm-preview-label">Footer preview</p>
                                                    <img src={footerPreview} alt="Footer Preview" className="itm-preview-img itm-preview-img--footer" />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <HeaderFooterEditor
                                            label="Footer"
                                            value={footerContentHtml}
                                            onChange={setFooterContentHtml}
                                            heightPx={FOOTER_HEIGHT_PX}
                                            onOverflowChange={setFooterOverflow}
                                            onUploadLogo={(file) =>
                                                uploadToBlob(file, `logo_footer_${auth.CompId}_${Date.now()}.${file.name.split(".").pop()}`)
                                            }
                                            placeholder="Type your footer text, and use the image icon in the toolbar to insert your logo…"
                                        />
                                    )}
                                </div>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="text-end">
                                <button className="btn btn-cancel-red me-2" title="Close" onClick={handleCloseModal}>
                                    Close
                                </button>
                                {(headerHasContent || footerHasContent) && (
                                    <button
                                        className="btn btn-outline-primary me-2"
                                        title="Download"
                                        onClick={() => setShowPreviewModal(true)}
                                    >
                                        Preview Invoice
                                    </button>
                                )}
                                <button
                                    className="btn btn-primary"
                                    title="Download"
                                    onClick={handleSave} disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save Template"}
                                </button>
                            </div>
                        </Modal.Footer>
                    </Modal>

                    {/* Invoice Preview Modal */}
                    <Modal className="itm-modal" show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                Invoice Preview with Template
                                <div>
                                    <small className="text-light">
                                        {headerHasContent && footerHasContent ? "Header & Footer" :
                                            headerHasContent ? "Header Only" :
                                                footerHasContent ? "Footer Only" : "No Header/Footer"}
                                    </small>
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                            <div style={{
                                border: "1px solid #ddd",
                                padding: "10px",
                                backgroundColor: "#f8f9fa",
                                fontFamily: "Arial, sans-serif"
                            }}>
                                {/* Header */}
                                {headerMode === "custom" && headerHasContent ? (
                                    <div
                                        className="headerImg"
                                        style={{ display: "block", width: "100%", height: "3cm", overflow: "hidden" }}
                                        dangerouslySetInnerHTML={{ __html: headerContentHtml }}
                                    />
                                ) : headerPreview && (
                                    <div className="headerImg">
                                        <img
                                            src={headerPreview}
                                            alt="Header"
                                            style={{ display: "block", width: "100%", height: "3cm", objectFit: "cover" }}
                                        />
                                    </div>
                                )}

                                {/* Invoice Content - Using exact structure from NewPrintInvoice.js */}
                                <div className={!headerHasContent && !footerHasContent ? "MobileViewInvoice" : "MobileViewInvoiceWOSpace"}>
                                    <table cellSpacing="0" cellPadding="0" style={{ border: "none", width: "100%" }}>
                                        <tbody>
                                            <div className="row">
                                                <div className="col-7 pe-0">
                                                    <p style={{ marginBottom: "2px", fontSize: "10px", textAlign: "right", fontWeight: "bold", textDecoration: "underline" }}>Tax Invoice</p>
                                                    <div style={{ marginLeft: "6px" }}>
                                                        <tr style={{ float: "right", marginTop: "10px" }}>
                                                            <td>
                                                                <b><span id="Label2">Distributor PAN No</span></b>
                                                            </td>
                                                            <td style={{ paddingLeft: "6px" }}>: <span>ABCDE1234F</span>
                                                            </td>
                                                        </tr>
                                                    </div>
                                                </div>
                                                <div className="col-5">
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">Invoice No</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>INV/000001</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">Invoice Date</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>{invoicedate}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">S.O / P.O No.</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>SO-001</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">S.O / P.O Date</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>{invoicedate}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">Mode of Payment</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>Credit</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">Payment Terms</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>14 Days Credit</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <b><span id="Label2">Distributor GSTN</span></b>
                                                        </td>
                                                        <td style={{ paddingLeft: "6px" }}>: <span>27AABCU9603R1ZX</span>
                                                        </td>
                                                    </tr>
                                                </div>
                                            </div>
                                        </tbody>
                                    </table>

                                    <table cellSpacing="0" cellPadding="0" style={{ border: "none" }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: "300px", height: "15px", verticalAlign: "top", paddingRight: "10px" }}>
                                                    <p id="Label7" style={{ fontSize: "8px", marginBottom: "2px" }}><b>Customer Address :</b></p>
                                                    <p id="lblrilcustcode" style={{ marginBottom: "-2px" }}>
                                                        CUST0001 &nbsp;
                                                        <b>PID</b> : 1234567890<br />
                                                        <b>PAN</b><br />
                                                        <b>SAMPLE CUSTOMER PVT LTD</b>
                                                        <br />
                                                        123, ABC BUILDING, SAMPLE STREET
                                                        <br />
                                                        XYZ INDUSTRIAL AREA, SECTOR 15
                                                        <br />
                                                        MUMBAI
                                                        <br />
                                                        Mumbai (27) MAHARASHTRA Pin: 400001
                                                    </p>
                                                </td>
                                                <td style={{ width: "300px", height: "15px", verticalAlign: "top" }}>
                                                    <b>
                                                        <p id="Label8" style={{ fontSize: "8px", marginBottom: "2px" }}>Delivery Address :</p>
                                                    </b>
                                                    <p id="lblShipToName1" style={{ marginBottom: "2px" }}>
                                                        456, DELIVERY LOCATION
                                                        <br />
                                                        SAMPLE INDUSTRIAL PARK, SECTOR 20
                                                        <br />
                                                        PUNE
                                                        <br />
                                                        Pune
                                                        <br />
                                                        (27) MAHARASHTRA
                                                        <br />
                                                        Pin : 411001
                                                        <br />
                                                        Phone : / 9876543210
                                                    </p>
                                                </td>
                                                <td style={{ width: "300px", height: "15px", verticalAlign: "bottom" }}>
                                                    <span className="d-inline-block" style={{ width: "60px", height: "60px", border: "1px solid #000", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px" }}>
                                                        QR<br />Code
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <div className="col-12 ">
                                        <p style={{ fontSize: "8px", marginBottom: "0px" }}>
                                            B.T GSTN :
                                        </p>
                                    </div>

                                    <div style={{ minHeight: "210px" }}>
                                        <div style={{ minHeight: "200px", display: "inline-block", width: "100%", position: "relative", border: "1px solid #000", padding: "1px" }} >
                                            <div id="tbl01">
                                                <table id="tblDetl" border="0" cellSpacing="0" cellPadding="0" width="100%">
                                                    <tbody>
                                                        <tr className="headerClass" style={{ borderBottom: "1px solid #000", lineHeight: "8px" }}>
                                                            <td align="left" style={{ width: "15px" }}><span style={{ textAlign: "left" }}>Sr<br />No</span></td>
                                                            <td align="left" style={{ width: "120px" }}><span style={{ textAlign: "left" }}>Product Pack Description</span></td>
                                                            <td align="right" style={{ width: "30px" }}><span style={{ textAlign: "right" }}>HSN</span></td>
                                                            <td align="right" style={{ width: "30px" }}><span style={{ textAlign: "right" }}>Rate <br />Per<br />Pack</span></td>
                                                            <td align="right" style={{ width: "30px" }}><span style={{ textAlign: "right" }}>MRP</span></td>
                                                            <td align="right" style={{ width: "30px" }}><span style={{ textAlign: "right" }}>No. of<br />Packs</span></td>
                                                            <td align="right" style={{ width: "40px" }}><span style={{ textAlign: "right" }}>Quantity<br />(EA/Kg/Ltrs)</span></td>
                                                            <td align="right" style={{ width: "40px" }}><span style={{ textAlign: "right" }}>Scheme/<br />Flat Disc.</span></td>
                                                            <td align="right" style={{ width: "35px" }} ><span style={{ textAlign: "right" }}>Surcharge</span></td>
                                                            <td align="right" style={{ width: "35px" }}><span style={{ textAlign: "right" }}>Taxable<br />Value</span></td>
                                                            <td align="right" style={{ width: "35px" }}><span style={{ textAlign: "right" }}>CGST Rs.<br />Rate%</span></td>
                                                            <td align="right" style={{ width: "35px" }}><span style={{ textAlign: "right" }}>SGST Rs.<br />Rate%</span></td>
                                                        </tr>
                                                        <tr style={{ height: "22px" }}>
                                                            <td align="left"><span style={{ textAlign: "left" }}>1</span></td>
                                                            <td align="left"><span style={{ textAlign: "left", wordBreak: "break-all" }}>Sample Product - Pack Description</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>12345</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>412.57</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>573.00</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>1</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>1</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>0.00<p className="mb-0">0.00</p></span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>0.00</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>412.57</span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>37.16<p className="mb-0">9.00</p></span></td>
                                                            <td align="right"><span style={{ textAlign: "right" }}>37.16<p className="mb-0">9.00</p></span></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: "-10px" }}>
                                        <table border="0" cellSpacing="0" cellPadding="4">
                                            <tbody>
                                                <tr>
                                                    <td align="left" valign="top" style={{ width: "300px" }}>
                                                        <b>
                                                            <span id="Label9">Receiver&apos;s Signature Date &amp; Stamp</span></b>
                                                    </td>
                                                    <td align="right" style={{ width: "198px" }}>
                                                        <b>
                                                            <span id="Label10">Total</span></b>
                                                    </td>
                                                    <td align="right" style={{ width: "27px" }}>
                                                        <span id="lblSumQty">1.00</span>
                                                    </td>
                                                    <td align="right" style={{ width: "70px" }}>
                                                        <span id="lblSumVol">1.00</span>
                                                    </td>
                                                    <td align="right" style={{ width: "75px", paddingBottom: "0px" }}>
                                                        <span id="lblSumScheme">0.00</span>
                                                    </td>
                                                    <td align="right" style={{ width: "70px" }}>
                                                        <span id="lblSumScheme">0.00</span>
                                                    </td>
                                                    <td align="right" style={{ width: "70px" }}>
                                                        <span id="lblSumAmt">412.57</span>
                                                    </td>
                                                    <td align="right" style={{ width: "60px" }}>
                                                        <span id="lblSumSGST">37.16</span>
                                                    </td>
                                                    <td align="right" style={{ width: "57px" }}>
                                                        <span id="lblSumCGST">37.16</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td align="left" style={{ width: "300px" }}></td>
                                                    <td align="right" style={{ width: "198px" }}></td>
                                                    <td align="right" style={{ width: "27px" }}></td>
                                                    <td align="right" style={{ width: "70px" }}></td>
                                                    <td align="right" style={{ width: "75px", paddingTop: "0px" }}>
                                                        <span id="lblSumCoupon">0.00</span>
                                                    </td>
                                                    <td align="right" style={{ width: "70px" }}></td>
                                                    <td align="right" style={{ width: "70px" }}></td>
                                                    <td align="right" style={{ width: "60px" }}></td>
                                                    <td align="right" style={{ width: "57px" }}></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ padding: "5px" }}>
                                        <table border="0" cellSpacing="0" cellPadding="0" style={{ lineHeight: "16px" }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ width: "421px" }}></td>
                                                    <td style={{ width: "190px", textAlign: "right" }}>
                                                        <b>
                                                            <span id="Label11">Gross Amount</span></b>
                                                    </td>
                                                    <td align="right" style={{ width: "56px" }}>
                                                        <span id="lblGrossAmt">₹ 412.57</span>
                                                    </td>
                                                    <td rowSpan="6" valign="top" align="center" className="cnTdWidth" style={{ paddingLeft: "10px", width: "95px" }}>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <div style={{ paddingBottom: "4px" }}>
                                                            <span style={{ marginTop: "10px", fontSize: "8px", fontWeight: "bold" }}>
                                                                Flat Discount = <span>₹ 0.00</span>
                                                            </span></div>
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        <b>
                                                            <span id="Label12">Other Discount</span></b>
                                                    </td>
                                                    <td align="right">
                                                        <span id="lblOthDisc">₹ 0.00</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td valign="top" style={{ paddingBottom: "4px" }}>
                                                        <b>
                                                            <span id="Label53">Scheme Benefit (inclusive of GST) = </span></b>
                                                        &nbsp;
                                                        <span id="lblSchemeBenefit" style={{ paddingLeft: "5px" }}>₹ 0.00</span>
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        <b>
                                                            <span id="Label13">Total Adjusted Amount</span></b>
                                                    </td>
                                                    <td align="right">
                                                        <span id="lblAdjAmt">₹ 0.00</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td valign="top">
                                                        <b>
                                                            <span id="Label14">Amount Payable (in Words)</span></b>
                                                    </td>
                                                    <td align="right"><b>
                                                        <span id="Label15">GST</span></b></td>
                                                    <td align="right">
                                                        <span id="lblTotalVat">₹ 74.32</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td valign="top">
                                                        <b>
                                                            <span id="Label14"></span></b>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td rowSpan="4" valign="top">
                                                        <b>
                                                            <span id="Label16" className="lblRs">Rupees : &nbsp;</span></b><span id="lblAmtInWord" className="amtWord">Four Hundred Eighty-Seven only</span>
                                                        <br />
                                                        <br />
                                                        <div style={{ border: "1px solid #333", padding: "2px", display: "inline-block", fontWeight: "bold", fontSize: "9px" }}>
                                                            <span id="Label58">Customer Service Toll Free No:1800222100/18002098100</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "right" }}>
                                                        <b>
                                                            <span id="Label55">Grand Total</span></b>
                                                    </td>
                                                    <td align="right">
                                                        <span id="lblTotal">₹ 487.00</span>
                                                    </td>
                                                </tr>
                                                <tr style={{ borderTop: "1px solid #000" }}>
                                                    <td className="borderTop txtRgt" style={{ textAlign: "right" }}>
                                                        <b>
                                                            <span id="LblCrdAdj1">Credit Adjustment</span></b>
                                                    </td>
                                                    <td className="borderTop crdPadd txtRgt" style={{ textAlign: "right" }}>
                                                        <span id="LblCrdAdjVal1">₹ 0.00</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="txtRgt" style={{ textAlign: "right" }}>
                                                        <b>
                                                            <span id="Label17">Amount Payable</span></b>
                                                    </td>
                                                    <td className="crdPadd txtRgt" style={{ textAlign: "right" }}>
                                                        <span id="lblGrandTotal">₹ 487.00</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-12 printmsg">
                                        <p style={{ fontSize: "8px", padding: "0px 60px 0px 0px", fontWeight: "bold", float: "right", marginBottom: "0px" }}>Print Message</p>
                                    </div>
                                </div>

                                {/* Footer */}
                                {footerMode === "custom" && footerHasContent ? (
                                    <div
                                        className="footerImg"
                                        style={{ display: "block", width: "100%", height: "6.5cm", overflow: "hidden" }}
                                        dangerouslySetInnerHTML={{ __html: footerContentHtml }}
                                    />
                                ) : footerPreview && (
                                    <div className="footerImg">
                                        <img
                                            src={footerPreview}
                                            alt="Footer"
                                            style={{ display: "block", width: "100%", height: "6.5cm", objectFit: "cover" }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="btn btn-cancel-red" onClick={() => setShowPreviewModal(false)}>
                                Close
                            </button>
                        </Modal.Footer>
                    </Modal>
                </div>
            </section>
        </div>


    );
};

export default InvoiceTemplateManager;